import React, { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ActiveFinancedCarousel from "./ActiveFinancedCarousel";
import AdvanceSearchDrawer from "./AdvanceSearchDrawer";
import ListingCard from "./ListingCard";
import ListingDrawer from "./ListingDrawer";
import ActiveFinancedDrawer from "./ActiveFinancedDrawer";
import getServices from "../../Services/get-service";
import Apa1 from "../../Assets/images/apa1.png";
import Note from "../../Assets/images/note.svg";
import { toast } from "react-toastify";

const PAGE_SIZE = 20;
// Format dates
const formatDate = (dateVal, formatStr = "dd MMM yyyy") => {
  if (!dateVal) return "";

  try {
    const safeDate =
      typeof dateVal === "string" ? dateVal.replace(" ", "T") : dateVal;

    const date = new Date(safeDate);

    if (Number.isNaN(date.getTime())) return "";

    return format(date, formatStr);
  } catch {
    return "";
  }
};

// Format covered area
const formatArea = (value) => {
  if (value == null || value === "") return "";

  const area = String(value).trim();

  return /[a-zA-Z²]/.test(area) ? area : `${area} sq. m`;
};

// Extract projects from API response
const getProjects = (response) => {
  const result = response.data;

  if (result?.statusCode !== 200) {
    throw new Error(result?.description || "Unable to retrieve listings");
  }

  return result.data.projects;
};

function MainSection() {
  // Search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);

  // Listings
  const [listings, setListings] = useState([]);
  const [activeListings, setActiveListings] = useState([]);
  const [savedIds, setSavedIds] = useState(() => new Set());

  // Loading
  const [loading, setLoading] = useState(true);
  const [activeLoading, setActiveLoading] = useState(true);

  //  pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);

  // Listing detail drawer
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Active financed drawer
  const [selectedFinancedItem, setSelectedFinancedItem] = useState(null);
  const [isFinancedDrawerOpen, setIsFinancedDrawerOpen] = useState(false);

  // FETCH PAGINATED LISTINGS
  useEffect(() => {
    let cancelled = false;

    const fetchListings = async () => {
      setLoading(true);

      try {
        const response = await getServices.getListing(PAGE_SIZE, currentPage);

        const projects = getProjects(response);

        const responseData = response.data.data;

        // Map listings
        const mappedListings = projects.map((item) => ({
          id: item.id ?? "",

          title: item.title ?? "",

          location:
            [item.street, item.locality].filter(Boolean).join(", ") || "",

          description: item.description ?? "",

          dateAdded: formatDate(item.createdAt),

          date: formatDate(item.createdAt, "d MMM yyyy"),

          price: item.price ?? 0,

          developerName: item.developerName ?? "",

          type: item.projectType ?? "",

          image: item.projectImages?.[0]?.imageUrl ?? Apa1,

          areaCovered: formatArea(item.coveredAreas),

          percentageSold: item.percentageSold ?? 0,

          unitsLeft: item.unitRemaining ?? 0,

          totalUnits: item.totalUnit ?? 0,

          developerPhone: item.phoneNumber ?? "",

          pricePerUnit: item.unitPrice ?? 0,
        }));

        // Pagination metadata
        const rawTotalPages = responseData?.totalPages;

        const parsedTotalPages =
          rawTotalPages == null ? null : Number(rawTotalPages);

        const availableTotalPages =
          Number.isInteger(parsedTotalPages) && parsedTotalPages >= 0
            ? parsedTotalPages
            : null;

        let nextPageExists = false;

        if (availableTotalPages !== null) {
          nextPageExists = currentPage + 1 < availableTotalPages;
        } else if (projects.length === PAGE_SIZE) {
          try {
            const nextResponse = await getServices.getListing(
              PAGE_SIZE,
              currentPage + 1,
            );

            const nextProjects = getProjects(nextResponse);

            nextPageExists = nextProjects.length > 0;
          } catch {
            nextPageExists = true;
          }
        }

        if (cancelled) return;

        setListings(mappedListings);
        setTotalPages(availableTotalPages);
        setHasNextPage(nextPageExists);
      } catch (error) {
        if (cancelled) return;

        setListings([]);
        setHasNextPage(false);
        setTotalPages(null);

        toast.error(error?.response?.data?.description);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchListings();

    return () => {
      cancelled = true;
    };
  }, [currentPage]);

  useEffect(() => {
    let cancelled = false;

    const fetchAdditionalListings = async () => {
      setActiveLoading(true);

      try {
        const [savedResult, activeResult] = await Promise.allSettled([
          getServices.getProjectBuyLater(100, 0),
          getServices.getActiveListings(),
        ]);

        if (cancelled) return;

        // Saved projects
        if (
          savedResult.status === "fulfilled" &&
          savedResult.value?.data?.statusCode === 200
        ) {
          const savedProjects =
            savedResult.value.data.data?.savedProjects ?? [];

          setSavedIds(
            new Set(savedProjects.map((project) => String(project.id))),
          );
        }

        // Active financed listings
        if (activeResult.status === "fulfilled") {
          const response = activeResult.value.data;

          if (response?.statusCode !== 200) {
            setActiveListings([]);

            toast.error(
              response?.description || "Unable to retrieve active listings",
            );
          } else {
            const activeProjects = response.data?.projects ?? [];

            const mappedActiveListings = activeProjects.map((item) => ({
              id: item.id ?? "",

              walletId: item.walletId ?? "",

              title: item.projectName ?? "",

              location: item.location ?? "",

              type:
                item.listingType === "LAND_LISTING" ||
                item.projectType === "LAND_LISTING"
                  ? "Land"
                  : "Property",

              price: item.amount ?? 0,

              pricePerUnit: item.unitPrice ?? 0,

              unitsHeld: item.unitsHeld ?? 0,

              unitsFinanced: item.unitsHeld ?? 0,

              amountFinanced: item.amount ?? 0,

              dateFinanced: formatDate(item.dateFinanced ?? item.createdAt),

              certificateDate: formatDate(item.dateFinanced ?? item.createdAt),

              transactionDate: "",

              transactions: [],

              updates: [],

              unitsLeft: item.unitRemaining ?? 0,

              totalUnits: item.totalUnit ?? 0,

              areaCovered: formatArea(item.coveredAreas),

              percentageSold: item.percentageSold ?? 0,

              targetCapital: item.price ?? 0,

              raised: item.amountRaised ?? 0,

              developerName: item.developer ?? item.developerName ?? "",

              developerPhone: item.phoneNumber ?? "",

              description: item.description ?? "",

              image: item.projectImages?.[0]?.imageUrl ?? Apa1,
            }));

            setActiveListings(mappedActiveListings);
          }
        } else {
          setActiveListings([]);

          toast.error(activeResult.reason?.response?.data?.description);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.response?.data?.description);
        }
      } finally {
        if (!cancelled) {
          setActiveLoading(false);
        }
      }
    };

    fetchAdditionalListings();

    return () => {
      cancelled = true;
    };
  }, []);

  // LISTING DRAWERS
  const handleOpenDetail = (item) => {
    setSelectedListingId(item.id);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedListingId(null);
  };

  const handleOpenFinancedDetail = (item) => {
    setSelectedFinancedItem(item);
    setIsFinancedDrawerOpen(true);
  };

  const handleCloseFinancedDetail = () => {
    setIsFinancedDrawerOpen(false);
    setSelectedFinancedItem(null);
  };

  // ADVANCED FILTERS
  const handleApplyAdvancedFilters = (filters) => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const handleRemoveFilter = (filterKey) => {
    if (!appliedFilters) return;

    const newFilters = { ...appliedFilters };

    delete newFilters[filterKey];

    setAppliedFilters(Object.keys(newFilters).length > 0 ? newFilters : null);

    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setAppliedFilters(null);
    setSearchQuery("");
    setCurrentPage(0);
  };

  // UPDATE BOOKMARK STATE
  const handleSaveToggle = (id, newSavedState) => {
    setSavedIds((prev) => {
      const updated = new Set(prev);

      if (newSavedState) {
        updated.add(String(id));
      } else {
        updated.delete(String(id));
      }

      return updated;
    });
  };

  // FILTER CURRENT PAGE LISTINGS
  const filteredListings = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return listings.filter((item) => {
      if (query) {
        const searchableText = [
          item.title,
          item.location,
          item.description,
          item.type,
        ]
          .join(" ")
          .toLowerCase();

        if (!searchableText.includes(query)) {
          return false;
        }
      }

      if (!appliedFilters) return true;

      // Listing type
      if (appliedFilters.listingType && appliedFilters.listingType !== "All") {
        const isLand = item.type === "LAND_LISTING" || item.type === "Land";

        const listingType = isLand ? "Land" : "Property";

        if (
          listingType.toLowerCase() !==
          appliedFilters.listingType.replace("_LISTING", "").toLowerCase()
        ) {
          return false;
        }
      }

      // Location
      if (
        appliedFilters.location &&
        appliedFilters.location !== "All Locations" &&
        !item.location
          .toLowerCase()
          .includes(appliedFilters.location.toLowerCase())
      ) {
        return false;
      }

      // Minimum price
      if (appliedFilters.minPrice) {
        const minPrice = Number(
          String(appliedFilters.minPrice).replace(/,/g, ""),
        );

        if (Number.isFinite(minPrice) && Number(item.price) < minPrice) {
          return false;
        }
      }

      // Maximum price
      if (appliedFilters.maxPrice) {
        const maxPrice = Number(
          String(appliedFilters.maxPrice).replace(/,/g, ""),
        );

        if (Number.isFinite(maxPrice) && Number(item.price) > maxPrice) {
          return false;
        }
      }

      return true;
    });
  }, [listings, searchQuery, appliedFilters]);

  // FILTER CHIPS
  const filterChips = useMemo(() => {
    if (!appliedFilters) return [];

    const chips = [];

    if (appliedFilters.listingType && appliedFilters.listingType !== "All") {
      chips.push({
        key: "listingType",
        label: `Listing Type = ${appliedFilters.listingType}`,
      });
    }

    if (
      appliedFilters.location &&
      appliedFilters.location !== "All Locations"
    ) {
      chips.push({
        key: "location",
        label: `Location = ${appliedFilters.location}`,
      });
    }

    if (appliedFilters.minPrice) {
      chips.push({
        key: "minPrice",
        label: `Min Price = ${appliedFilters.minPrice}`,
      });
    }

    if (appliedFilters.maxPrice) {
      chips.push({
        key: "maxPrice",
        label: `Max Price = ${appliedFilters.maxPrice}`,
      });
    }

    return chips;
  }, [appliedFilters]);

  // SERVER-SIDE PAGINATION
  const handleNextPage = () => {
    if (loading || !hasNextPage) return;

    setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (loading || currentPage === 0) return;

    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  return (
    <div className="pb-12">
      {/* ACTIVE FINANCED CAROUSEL */}
      {activeLoading ? (
        <div className="mb-8 flex gap-4 overflow-hidden">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className="w-[280px] h-[140px] bg-gray-200 rounded-2xl animate-pulse flex-shrink-0"
            />
          ))}
        </div>
      ) : activeListings.length > 0 ? (
        <ActiveFinancedCarousel
          items={activeListings}
          onCardClick={handleOpenFinancedDetail}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center flex flex-col items-center justify-center mb-8">
          <img src={Note} alt="" className="w-12 h-12 opacity-50 mb-2" />

          <h3 className="text-gray-700 font-medium text-sm mb-1">
            No active financed listings
          </h3>
        </div>
      )}

      {/* SEARCH */}
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative flex items-center">
            <div className="absolute left-4 text-gray-400 pointer-events-none flex items-center">
              <SearchIcon sx={{ fontSize: 19 }} />
            </div>

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
              placeholder="Search listing by name or location"
              aria-label="Search listings"
              className="w-full bg-white border border-[#E5E7EB] text-gray-800 text-sm rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-1 focus:ring-[#00BC67] transition-all placeholder:text-gray-400"
            />

            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(0);
                }}
                aria-label="Clear search"
                className="absolute right-3.5 text-gray-400 hover:text-gray-600 p-1"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>

          {/* Advanced Search Button */}
          <button
            type="button"
            onClick={() => setIsAdvancedOpen(true)}
            className="flex items-center gap-2 bg-white border border-[#E5E7EB] hover:bg-gray-50 text-[#1E222B] text-sm font-medium py-3 px-5 rounded-xl transition-all shadow-2xs whitespace-nowrap cursor-pointer"
          >
            <FilterAltOutlinedIcon
              sx={{
                fontSize: 18,
                color: "#1E222B",
              }}
            />

            <span>Advanced Search</span>
          </button>
        </div>

        {/* APPLIED FILTERS */}
        {filterChips.length > 0 && (
          <div className="flex items-center gap-2.5 mt-3 flex-wrap">
            {filterChips.map((chip) => (
              <div
                key={chip.key}
                className="inline-flex items-center gap-1.5 bg-[#F1F5F9] text-[#1E222B] text-[11px] font-medium py-1 px-3 rounded-full border border-gray-200/60"
              >
                <span>{chip.label}</span>

                <button
                  type="button"
                  onClick={() => handleRemoveFilter(chip.key)}
                  className="text-gray-400 hover:text-gray-700 rounded-full p-0.5 ml-0.5 transition cursor-pointer"
                  aria-label={`Remove filter ${chip.label}`}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs text-red-500 hover:text-red-700 underline underline-offset-2 ml-1 cursor-pointer font-medium"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* LISTINGS */}
      <div className="mt-4">
        {loading ? (
          // Loading skeleton
          <div className="space-y-4">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4 flex flex-col md:flex-row gap-5 items-start animate-pulse"
              >
                <div className="w-full md:w-[270px] lg:w-[290px] h-[180px] md:h-[185px] rounded-xl bg-gray-200 flex-shrink-0" />

                <div className="flex-1 flex flex-col justify-between w-full min-h-[180px] space-y-3">
                  <div>
                    <div className="flex justify-between items-start">
                      <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                      <div className="h-4 bg-gray-100 rounded w-24" />
                    </div>

                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />

                    <div className="h-3 bg-gray-100 rounded w-full mb-1" />

                    <div className="h-3 bg-gray-100 rounded w-4/5" />
                  </div>

                  <div>
                    <div className="h-3 bg-gray-200 rounded w-1/4 mb-2" />

                    <div className="h-2 bg-gray-200 rounded-full w-48 mb-3" />

                    <div className="flex justify-between items-center pt-2">
                      <div className="h-4 bg-gray-100 rounded w-1/2" />

                      <div className="h-6 bg-gray-200 rounded w-24" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredListings.length > 0 ? (
          filteredListings.map((listing) => (
            <ListingCard
              key={listing.id}
              {...listing}
              isSaved={savedIds.has(String(listing.id))}
              onSaveToggle={handleSaveToggle}
              onClick={() => handleOpenDetail(listing)}
            />
          ))
        ) : (
          // Empty state
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center flex flex-col items-center justify-center my-6">
            <img src={Note} alt="" className="w-16 h-16 opacity-50 mb-3" />

            <h3 className="text-gray-700 font-medium text-base mb-1">
              No listings found
            </h3>

            <p className="text-gray-400 text-xs max-w-sm mb-4">
              We couldn't find any listings matching your search or filters. Try
              clearing some filters or searching for something else.
            </p>

            {(appliedFilters || searchQuery) && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="bg-[#00BC67] text-white text-xs font-medium px-4 py-2 rounded-lg hover:bg-[#00a85c] transition"
              >
                Reset all filters
              </button>
            )}
          </div>
        )}

        {/* SERVER-SIDE PAGINATION */}
        {!loading && (currentPage > 0 || hasNextPage) && (
          <nav
            aria-label="Listing pagination"
            className="mt-8 flex items-center justify-center gap-3 sm:justify-end"
          >
            {/* Previous */}
            <button
              type="button"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
              className="rounded-lg border border-[#E6E6E7] px-4 py-2 text-sm font-medium transition-colors hover:bg-[#00BC67] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>

            {/* Current Page */}
            <span className="text-sm text-[#6B7281]" aria-live="polite">
              {currentPage + 1}
            </span>

            {/* Next */}
            <button
              type="button"
              onClick={handleNextPage}
              disabled={!hasNextPage}
              className="rounded-lg border border-[#E6E6E7] px-4 py-2 text-sm font-medium transition-colors hover:bg-[#00BC67] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        )}
      </div>

      {/* ADVANCED SEARCH DRAWER */}
      <AdvanceSearchDrawer
        open={isAdvancedOpen}
        handleClose={() => setIsAdvancedOpen(false)}
        initialFilters={appliedFilters ?? {}}
        onApplyFilters={handleApplyAdvancedFilters}
      />

      {/* LISTING DETAIL DRAWER */}
      <ListingDrawer
        open={isDetailOpen}
        handleClose={handleCloseDetail}
        id={selectedListingId}
      />

      {/* ACTIVE FINANCED DETAIL DRAWER */}
      <ActiveFinancedDrawer
        open={isFinancedDrawerOpen}
        handleClose={handleCloseFinancedDetail}
        details={selectedFinancedItem ?? {}}
      />
    </div>
  );
}

export default MainSection;
